import { useMemo } from 'react';

type Candle = {
  open: number;
  high: number;
  low: number;
  close: number;
};

function buildCandles(openPrice: number, closePrice: number, pipSize: number, count: number): Candle[] {
  const candles: Candle[] = [];
  let prevClose = openPrice;
  for (let i = 0; i < count; i++) {
    const t = (i + 1) / count;
    const target = openPrice + (closePrice - openPrice) * t;
    const wave = Math.sin(i * 1.35 + 0.4) * pipSize * 2;
    const close = target + wave;
    const open = prevClose;
    const high = Math.max(open, close) + pipSize * (1.2 + (i % 3) * 0.6);
    const low = Math.min(open, close) - pipSize * (1.1 + (i % 2) * 0.7);
    candles.push({ open, high, low, close });
    prevClose = close;
  }
  return candles;
}

function formatPrice(price: number, pipSize: number): string {
  return pipSize >= 0.01 ? price.toFixed(2) : price.toFixed(4);
}

type KlineSparklineProps = {
  openPrice: number;
  closePrice: number;
  pipSize: number;
  direction: 'buy' | 'sell';
  profit: boolean;
};

const upColor = '#059669';
const downColor = '#e11d48';

export default function KlineSparkline({
  openPrice,
  closePrice,
  pipSize,
  direction,
  profit,
}: KlineSparklineProps) {
  const candles = useMemo(() => buildCandles(openPrice, closePrice, pipSize, 12), [
    openPrice,
    closePrice,
    pipSize,
  ]);

  const minLow = Math.min(...candles.map((c) => c.low));
  const maxHigh = Math.max(...candles.map((c) => c.high));
  const range = maxHigh - minLow || 1;
  const H = 72;

  const px = (price: number) => ((maxHigh - price) / range) * H;

  const trendColor = profit ? upColor : downColor;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-violet-100 bg-violet-50/50 p-3">
      <div
        className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full blur-2xl"
        style={{ background: `${trendColor}1f` }}
      />

      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className="flex h-6 w-6 items-center justify-center rounded-full"
            style={{ backgroundColor: `${trendColor}1a` }}
          >
            <i
              className={`${profit ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-sm`}
              style={{ color: trendColor }}
            />
          </span>
          <span className="text-xs font-semibold text-slate-700">
            {profit ? 'Xu hướng có lợi' : 'Xu hướng bất lợi'}
          </span>
        </div>
        <span className="font-mono text-[11px] font-semibold text-slate-400">
          {direction === 'buy' ? 'Mua · Long' : 'Bán · Short'}
        </span>
      </div>

      <div className="relative mt-3 flex items-end justify-between">
        <div className="flex items-end gap-[3px]">
          {candles.map((c, i) => {
            const bullish = c.close >= c.open;
            const bodyTop = px(Math.max(c.open, c.close));
            const bodyH = Math.max(px(Math.min(c.open, c.close)) - bodyTop, 1.5);
            const wickTop = px(c.high);
            const wickBottom = px(c.low);
            const color = bullish ? upColor : downColor;
            return (
              <div key={i} className="relative" style={{ width: 7, height: H }}>
                <span
                  className="absolute left-1/2 w-px -translate-x-1/2"
                  style={{ top: wickTop, height: bodyTop - wickTop, backgroundColor: color }}
                />
                <span
                  className="absolute left-1/2 w-[5px] -translate-x-1/2 rounded-[1px]"
                  style={{ top: bodyTop, height: bodyH, backgroundColor: color }}
                />
                <span
                  className="absolute left-1/2 w-px -translate-x-1/2"
                  style={{
                    top: bodyTop + bodyH,
                    height: wickBottom - (bodyTop + bodyH),
                    backgroundColor: color,
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="relative mt-2 flex items-center justify-between border-t border-violet-100 pt-2">
        <div className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          <span className="font-mono text-[11px] text-slate-500">Vào {formatPrice(openPrice, pipSize)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-[11px] text-slate-500">Đóng {formatPrice(closePrice, pipSize)}</span>
          <span className="h-2 w-2 rounded-full bg-rose-500" />
        </div>
      </div>
    </div>
  );
}